import { Box, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../../api/api';
import { useEffect, useState } from 'react';
import { LeadMatch } from '../../../components/exo/sales-object-matching/match-preview/LeadMatch';
import { ExoSkeleton } from '../../../components/exo/ExoSkeleton';
import Loader from '../../../components/special/Loader';
import { updateOrCreate } from '../../../components/special/updateOrCreate';

export const SalesObjectActiveLeads = ({ salesObjectId, apiData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [leads, setLeads] = useState(null);
  const [currentType, setCurrentType] = useState('active');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadActiveLeads();
  }, [config]);

  function loadActiveLeads() {
    DBRequest({
      config,
      path: `sales-objects/${salesObjectId}/matches?status[eq]=1`,
      onResponse: setLeads,
    });
  }

  // Sort levels once for convenience (ascending by accessLevel)
  const levelsSorted =
    apiData?.data?.levels
      ?.slice()
      .sort((a, b) => a.accessLevel - b.accessLevel) || [];

  // Find next higher level based on current accessLevel
  function getNextHigherLevel(currentLevel) {
    return levelsSorted.find((level) => level.accessLevel > currentLevel);
  }

  // Find next lower level based on current accessLevel
  function getNextLowerLevel(currentLevel) {
    // Find the highest level less than currentLevel
    return [...levelsSorted]
      .reverse()
      .find((level) => level.accessLevel < currentLevel);
  }

  function loadDeclinedLeads() {
    DBRequest({
      config,
      path: `sales-objects/${salesObjectId}/matches?status[eq]=2`,
      onResponse: setLeads,
    });
  }

  function handleTypeChange(e) {
    if (e.target.value === currentType) return;
    setCurrentType(e.target.value);
    setLeads(null);
    if (e.target.value === 'active') loadActiveLeads();
    if (e.target.value === 'declined') loadDeclinedLeads();
  }

  function handlePromote(match) {
    if (!match.accessPoint) return;

    const nextLevel = getNextHigherLevel(match.accessPoint.accessLevel);
    const accessLevel = nextLevel.accessLevel;

    DBRequest({
      config,
      path: `sales-object-access-points/${match.accessPoint.id}`,
      method: API_PATCH({
        accessLevel: accessLevel,
      }),
      onLoading: setIsLoading,
      onResponse: (res) =>
        handleUpdate({
          ...match,
          accessPoint: res.data,
        }),
    });
  }

  function handleDemote(match) {
    if (!match.accessPoint) return;

    const prevLevel = getNextLowerLevel(match.accessPoint.accessLevel);

    const accessLevel = prevLevel.accessLevel;
    DBRequest({
      config,
      path: `sales-object-access-points/${match.accessPoint.id}`,
      method: API_PATCH({
        accessLevel: accessLevel,
      }),
      onLoading: setIsLoading,
      onResponse: (res) =>
        handleUpdate({
          ...match,
          accessPoint: res.data,
        }),
    });
  }

  function handleUpdate(newMatch) {
    setLeads(updateOrCreate(leads, newMatch));
  }

  function handleRemove(match) {
    removeMatchFromMatches(match);
    DBRequest({
      config,
      path: `sales-objects/${salesObjectId}/matches`,
      method: API_POST({
        status: 2,
        matchId: match.id,
      }),
      onError: () => setLeads(leads),
    });
  }

  function handleAdd(match) {
    removeMatchFromMatches(match);
    DBRequest({
      config,
      path: `sales-objects/${salesObjectId}/matches`,
      method: API_POST({
        status: 1,
        matchId: match.id,
      }),
      onError: () => setLeads(leads),
    });
  }

  function removeMatchFromMatches(match) {
    const newMatches = leads.data.filter((obj) => obj.id !== match.id);
    setLeads({ ...leads, data: newMatches });
  }

  return (
    <Box className="px-3 py-2 rounded-lg w-full relative">
      <Loader active={isLoading} />
      <ToggleButtonGroup
        size="small"
        value={currentType}
        exclusive
        onChange={handleTypeChange}
        aria-label="Project Status"
        className="backdrop-blur-lg"
      >
        <ToggleButton value="active">{t('Active')}</ToggleButton>
        <ToggleButton value="declined">{t('Declined')}</ToggleButton>
      </ToggleButtonGroup>
      <Box className="flex flex-col gap-2 pt-2">
        {leads ? (
          leads?.data?.map((lead) => (
            <LeadMatch
              className="px-3 py-2 rounded-lg flex w-full lg:gap-4 lg:items-center flex-col lg:flex-row"
              sx={{ bgcolor: colors.card }}
              match={lead}
              onPromote={handlePromote}
              onDemote={handleDemote}
              onRemove={currentType === 'active' && handleRemove}
              onAdd={currentType === 'declined' && handleAdd}
              allowClick
              apiData={apiData}
            />
          ))
        ) : (
          <ExoSkeleton n={5} size={'60px'} />
        )}
      </Box>
    </Box>
  );
};
